import { useMemo } from 'react';
import OnelivePointList, { type PointListRow } from './OnelivePointList';
import {
  formatAmount,
  resolveDisplayNickname,
  type ExchangeItem,
  type ItemShopWallet,
} from './api';
import { useItemShopWalletData } from './useItemShopWalletData';
import { useOneliveSession } from './useOneliveSession';

interface Props {
  title?: string;
  showClose?: boolean;
  onClose?: () => void;
  /** 관리 헤더 레이아웃 안에 삽입될 때 상단 타이틀 바 숨김 */
  embedded?: boolean;
}

function orderStatusLabel(order: { status?: string; status_label?: string }): string {
  return order.status_label
    || (order.status === 'completed' ? '처리완료' : order.status === 'rejected' ? '부결처리' : '처리대기');
}

function buildCoinRows(wallet: ItemShopWallet | null, exchanges: ExchangeItem[]): PointListRow[] {
  const rows: PointListRow[] = [];

  if (wallet?.ledger && wallet.ledger.length > 0) {
    wallet.ledger.forEach((entry) => {
      const amount = Number(entry.amount ?? 0);
      rows.push({
        id: `ledger-${entry.id}`,
        title: entry.content || '코인 내역',
        amountText: `${amount >= 0 ? '+' : ''}${formatAmount(Math.abs(amount))}`,
        date: entry.processed_at,
        isMinus: amount < 0,
      });
    });
  } else {
    (wallet?.charges ?? []).forEach((charge, index) => {
      rows.push({
        id: `charge-${index}`,
        title: charge.note || '코인 충전',
        amountText: `+${formatAmount(charge.amount)}`,
        date: charge.charged_at,
        statusLabel: orderStatusLabel(charge),
      });
    });

    (wallet?.orders ?? []).forEach((order, index) => {
      rows.push({
        id: `order-${index}`,
        title: `[${order.item_name}] 주문신청 ${formatAmount(order.price)}`,
        amountText: `+${formatAmount(order.coin)}`,
        date: order.ordered_at,
        statusLabel: orderStatusLabel(order),
      });
    });
  }

  exchanges.forEach((item) => {
    if (wallet?.ledger?.length && item.status !== 'pending') {
      return;
    }
    rows.push({
      id: `exchange-${item.id}`,
      title: [item.account_holder, item.bank_name, item.account_number].filter(Boolean).join(' ') || item.content || '코인환전',
      amountText: `-${formatAmount(item.amount)}`,
      date: item.created_at,
      isMinus: true,
    });
  });

  return rows.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
}

export default function OneliveCoinHistoryPanel({
  title,
  showClose = false,
  onClose,
  embedded = false,
}: Props) {
  const { user } = useOneliveSession();
  const { loading, wallet, exchanges } = useItemShopWalletData(100);
  const nickname = resolveDisplayNickname(user);
  const panelTitle = title || `${nickname}님의 코인 내역`;

  const rows = useMemo(() => buildCoinRows(wallet, exchanges), [wallet, exchanges]);
  const plus = rows.filter((row) => !row.isMinus).reduce((sum, row) => sum + parseSigned(row.amountText), 0);
  const minus = rows.filter((row) => row.isMinus).reduce((sum, row) => sum + parseSigned(row.amountText), 0);

  return (
    <div
      id="point"
      className={[
        'new_win',
        'onelive-coin-history-panel',
        embedded ? 'onelive-coin-history-panel--embedded' : '',
      ].filter(Boolean).join(' ')}
    >
      {!embedded ? (
        <h1 id="win_title">
          {panelTitle}
          {showClose ? (
            <button type="button" className="onelive-coin-history-close" onClick={onClose} aria-label="닫기">
              ×
            </button>
          ) : null}
        </h1>
      ) : (
        <div className="onelive-mypage-history-head">
          <h2 className="onelive-mypage-history-head-title">{panelTitle}</h2>
        </div>
      )}
      <div className="new_win_con2 onelive-mypage-history-panel">
        {loading ? (
          <p className="onelive-loading">로딩 중...</p>
        ) : (
          <>
            <ul className="point_all">
              <li className="full_li">
                보유코인:
                <span>{formatAmount(wallet?.coin_balance ?? 0)}</span>
              </li>
            </ul>
            <OnelivePointList
              items={rows}
              emptyText="코인 내역이 없습니다."
              summaryPlus={plus}
              summaryMinus={minus}
            />
          </>
        )}
      </div>
    </div>
  );
}

function parseSigned(text: string): number {
  const normalized = text.replace(/[^\d-]/g, '');
  const value = Number(normalized);
  return Number.isFinite(value) ? Math.abs(value) : 0;
}
